import { useRef } from "react";
import styled from "styled-components";

/* Scroll Animation Library */
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { mobile } from "../utilities/responsive";
// import ScrollIndicator from "../Components/ScrollIndicator";
gsap.registerPlugin(ScrollTrigger);

/* Required Libraries: Font Awesome, ScrollTrigger from GSAP */
/* Required fonts: Poppins, Google Font */

/* TO DO */
/* Modularize Components, i.e. Reviews module */

const Container = styled.div`
  margin-left: 60px;
  font-family: Poppins, sans-serif;
  overflow-x: hidden;
  padding: 0;

  ${mobile({
    marginLeft: "clamp(30px, calc(5vw + 10px), calc(5vw + 10px))"
  })}
`;

const SecTitle = styled.p`
  font-size: calc(8vw + 4vh + 2vmin);
  position: absolute;
  z-index: -1;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  margin: 0;

  /* ${mobile({ fontSize: "25px" })} */

`;

const RevPin = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  margin: auto;
  height: max-content;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  pointer-events: none;

  ${mobile({
    position: "static",
    margin: "0 auto 6vh",
  })}
`;

const RTitle = styled.p`
  font-size: calc(8vw + 4vh + 2vmin);
  z-index: -1;
  margin: 0;
`;

const Cue = styled.span`
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: cueBounce 1.4s ease-in-out infinite;

  span {
    font-size: 0.8rem;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: #555;
  }

  &::after {
    content: "";
    width: 10px;
    height: 10px;
    margin-top: 6px;
    border-bottom: solid #555 2px;
    border-right: solid #555 2px;
    transform: rotate(45deg);
  }

  ${mobile({
    display: "none",
  })}

  @keyframes cueBounce {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(6px);
    }
  }
`;

const HeadTitle = styled.p`
  font-size: calc(8vw + 4vh + 2vmin);
  margin: 0;
`;

const HeadSection = styled.section`
  width: 100%;
  height: 80vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  position: relative;

  /* border-bottom: 1rem solid black; */

  ${mobile({ height: "80vh"})}
`;

const BottomSection = styled.section`
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-bottom: 25vh;
`;

const ReviewSection = styled.section`
  width: 100%;
  height: 80vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;

  ${mobile({
    height: "auto",
    padding: "8vh 0",
  })}
`;

const ReviewWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 0;
  gap: 2rem;
  row-gap: 15rem;

  ${mobile({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "1.5rem",
    // height: "100%",
  })}
`;

const Review = styled.div`
  width: 400px;
  height: 300px;
  box-shadow: 10px 0px 40px 0px rgba(0, 0, 0, 0.2);
  border-radius: 2rem;
  box-sizing: border-box;
  z-index: 2;
  background-color: white;
  padding: 3rem;

  ${mobile({
    width: "min(90vw, calc(100vw - 60px))",
    height: "auto",
  })}
`;

const ReviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.div`
  font-size: 2rem;
  margin: 0;
`;

const Content = styled.div``;

const Role = styled.div`
  margin-top: 1.2rem;
  font-size: 0.9rem;
  color: #888;
`;

const Feedback = () => {
  const main = useRef();

  useGSAP(
    () => {
      const mm = gsap.matchMedia();

      /* Sweeping card animation + pinned title are desktop-only;
         on mobile the cards stack and scroll normally. */
      mm.add("(min-width: 861px)", () => {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: ".reviews",
            start: "top 40%",
            end: "bottom top",
            scrub: 1,
          },
        });
        tl.fromTo(".rl", { y: "350%" }, { y: "-150%" }, 0);
        tl.fromTo(".rr", { y: "300%" }, { y: "-50%" }, 0);

        gsap.fromTo(
          ".rtitle",
          { x: "100%" },
          {
            x: "-150%",
            scrollTrigger: {
              trigger: ".rtitle",
              start: "center center",
              end: "bottom center",
              endTrigger: ".reviews",
              pin: ".rev-pin",
              scrub: 1,
            },
          },
          0
        );
      });

      /* Mobile: review cards slide in from alternating sides. */
      mm.add(
        "(max-width: 860px) and (prefers-reduced-motion: no-preference)",
        () => {
          gsap.utils.toArray(".rl, .rr").forEach((card) => {
            const fromLeft = card.classList.contains("rl");
            gsap.fromTo(
              card,
              { x: fromLeft ? -60 : 60, autoAlpha: 0 },
              {
                x: 0,
                autoAlpha: 1,
                duration: 0.7,
                ease: "power2.out",
                scrollTrigger: { trigger: card, start: "top 85%" },
              }
            );
          });
        }
      );

      return () => mm.revert();
    },
    { scope: main }
  );

  return (
    <Container ref={main}>
      {/* <ScrollIndicator color="000000" mobile="hidden" /> */}
      <HeadSection>
        <HeadTitle>AMAZING</HeadTitle>
        <Cue className="head-cue">
          <span>Scroll</span>
        </Cue>
      </HeadSection>
      <ReviewSection className="reviews">
        <RevPin className="rev-pin">
          <RTitle className="rtitle">FEEDBACK</RTitle>
        </RevPin>
        <ReviewWrapper>
          <Review className="rl">
            <ReviewHeader>
              <Title>MARCUS BELL</Title>
            </ReviewHeader>
            <Content>
              Ash was a great and engaging facilitator who even took some risk by improvising the last module for the benefit of the group.
            </Content>
            <Role>Defense Contractor</Role>
          </Review>
          <Review className="rr">
            <ReviewHeader>
              <Title>TAYLOR</Title>
            </ReviewHeader>
            <Content>
              One time we asked Ash to take a look at this problem we were having with load times and concurrent users, and he figured it out and probably saved us like two days of work.
            </Content>
            <Role>USAA</Role>
          </Review>
          <Review className="rl">
            <ReviewHeader>
              <Title>SARAH JENNINGS</Title>
            </ReviewHeader>
            <Content>
              Ash did a great job at being personable, making the training environment fun and interactive, and ensuring that we are comfortable with the new stuff we have learned!
            </Content>
            <Role>US Air Force</Role>
          </Review>
          <Review className="rr">
            <ReviewHeader>
              <Title>ANONYMOUS</Title>
            </ReviewHeader>
            <Content>
              Ash has great Jeff Goldblum energy.
            </Content>
          </Review>
        </ReviewWrapper>
      </ReviewSection>
      <BottomSection>
        <SecTitle>AMAZING</SecTitle>
      </BottomSection>
    </Container>
  );
};

export default Feedback;
